import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AllPermissions from "./AllPermissions";
import { useSelector } from "react-redux";
import { selectModules } from "../redux/userDataSlice";
import { selectToken } from "../redux/authSlice";
import { ApiCall } from "../helper/axios";
import CustomLoader from "./CustomLoader";
import useToast from "./Toast/useToast";
import "../App.css";
import AppButton from "./AppButton";
const drawerWidth = 800;
export default function OverrideRoleDrawer({ open, onClose, roleName = "Account Manager", userId, onSave, type, initialPermissions, initialOverrides }) {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const modulesFromRedux = useSelector(selectModules);
    const token = useSelector(selectToken);
    const toast = useToast();
    // Convert Redux modules to ModuleData format
    // Helper function to recursively convert sub_modules to sub_module
    const convertModule = (m) => {
        const subModules = m.sub_modules || m.sub_module || [];
        return {
            _id: m._id || m.id,
            key: m.key || m._id || m.id,
            module_name: m.module_name || m.name || "",
            order: m.order || 0,
            permissions: m.permissions || {},
            sub_module: subModules.map((sub) => convertModule(sub)),
            is_parent: m.is_parent || false,
            url: m.url || "",
        };
    };
    const modules = useMemo(() => {
        if (!modulesFromRedux || modulesFromRedux.length === 0) {
            return [];
        }
        return (modulesFromRedux || []).map((m) => convertModule(m));
    }, [modulesFromRedux]);
    const handleSave = async () => {
        if (!userId) {
            toast.error("Unable to save role override. User data is missing. Please close and try again.");
            return;
        }
        setLoading(true);
        try {
            // Transform permissions to API format, only including actual overrides (diff from role)
            const apiPermissions = {};
            Object.keys(permissions).forEach((moduleKey) => {
                const perm = permissions[moduleKey];
                // Get the default role permission for this module
                const rolePerm = initialPermissions?.[moduleKey] || {
                    access: false,
                    actions: [],
                    client_scope: "assigned",
                    refresh: false,
                };
                // Compare to see if it's actually different
                const isAccessDiff = (perm.access || false) !== (rolePerm.access || false);
                const isClientScopeDiff = (perm.client_scope || "assigned") !== (rolePerm.client_scope || "assigned");
                const isRefreshDiff = (perm.refresh || false) !== (rolePerm.refresh || false);
                // Compare actions arrays (sort before comparing to ignore order)
                const permActions = Array.isArray(perm.actions) ? [...perm.actions].sort() : [];
                const roleActions = Array.isArray(rolePerm.actions) ? [...rolePerm.actions].sort() : [];
                const isActionsDiff = JSON.stringify(permActions) !== JSON.stringify(roleActions);
                // Only add to apiPermissions if there is a difference
                if (isAccessDiff || isClientScopeDiff || isRefreshDiff || isActionsDiff) {
                    apiPermissions[moduleKey] = {
                        access: perm.access !== undefined ? perm.access : false,
                        actions: Array.isArray(perm.actions) ? perm.actions : [],
                        client_scope: perm.client_scope || "assigned",
                        refresh: perm.refresh !== undefined ? perm.refresh : false,
                    };
                }
            });
            // Payload structure: use 'overrides' key instead of 'permissions', don't include 'roleName'
            const payload = {
                overrides: apiPermissions,
            };
            // Make PUT API call to /api/users/{user_id}/overrides
            const res = await ApiCall("PUT", `/api/users/${userId}/overrides`, payload, {
                Authorization: token || "",
            });
            if (res && 'data' in res && res?.data?.success) {
                toast.success(res.data?.message || "Role override saved successfully");
                // Get the saved overrides from API response (if available)
                // API response format: { data: { user: { overrides: {...} } } } or { data: { overrides: {...} } }
                const responseData = res.data?.data;
                const savedOverrides = responseData?.user?.overrides || responseData?.overrides || apiPermissions;
                // Call onSave callback with override data
                if (onSave) {
                    onSave({
                        roleName: roleName,
                        permissions,
                        overrides: savedOverrides,
                        finalPermissions: res.data?.data?.permissions
                    });
                }
                onClose();
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || "Failed to save role override");
            }
        }
        catch (error) {
            console.error("Error saving role override:", error);
            toast.error("Failed to save role override. Please try again.");
        }
        finally {
            setLoading(false);
        }
    };
    const handlePermissionsChange = (newPermissions) => {
        setPermissions(newPermissions);
    };
    // Initialize permissions when drawer opens
    // Priority: initialOverrides > initialPermissions > module.permissions > defaults
    // Use overrides if available, otherwise fall back to permissions
    useEffect(() => {
        if (open && modules.length > 0) {
            const initialPerms = {};
            // Helper function to recursively get ALL modules
            const getAllModules = (moduleList) => {
                const all = [];
                moduleList.forEach((module) => {
                    all.push(module);
                    if (module.sub_module && module.sub_module.length > 0) {
                        all.push(...getAllModules(module.sub_module));
                    }
                });
                return all;
            };
            const allModules = getAllModules(modules);
            allModules.forEach((module) => {
                // Skip parent modules that don't need direct permissions (optional, but safer to include all)
                // Actually, let's include all to be sure.
                // Priority 1: Use initialOverrides if available (user-specific overrides)
                if (initialOverrides && initialOverrides[module.key]) {
                    const overridePerms = initialOverrides[module.key];
                    initialPerms[module.key] = {
                        access: overridePerms.access !== undefined ? overridePerms.access : false,
                        actions: Array.isArray(overridePerms.actions) ? overridePerms.actions : [],
                        client_scope: overridePerms.client_scope || "assigned",
                        refresh: overridePerms.refresh !== undefined ? overridePerms.refresh : false,
                    };
                }
                // Priority 2: Use initialPermissions if available (default permissions)
                else if (initialPermissions && initialPermissions[module.key]) {
                    const userPerms = initialPermissions[module.key];
                    initialPerms[module.key] = {
                        access: userPerms.access !== undefined ? userPerms.access : false,
                        actions: Array.isArray(userPerms.actions) ? userPerms.actions : [],
                        client_scope: userPerms.client_scope || "assigned",
                        refresh: userPerms.refresh !== undefined ? userPerms.refresh : false,
                    };
                }
                // Priority 3: If initialPermissions is provided but module.key is missing, the role has it turned off
                else if (initialPermissions) {
                    initialPerms[module.key] = {
                        access: false,
                        actions: [],
                        client_scope: "assigned",
                        refresh: false,
                    };
                }
                // Priority 4: Use module.permissions from modules API as default values (if no initialPermissions)
                else if (module.permissions !== undefined && module.permissions !== null) {
                    const modulePerms = module.permissions;
                    initialPerms[module.key] = {
                        access: modulePerms.access !== undefined ? modulePerms.access : false,
                        // Don't pre-select actions - user will select manually
                        actions: [],
                        client_scope: modulePerms.client_scope || "assigned",
                        refresh: modulePerms.refresh !== undefined ? modulePerms.refresh : false,
                    };
                }
                // Priority 4: Hardcoded default values
                else {
                    initialPerms[module.key] = {
                        access: false,
                        actions: [],
                        client_scope: "assigned",
                        refresh: false,
                    };
                }
            });
            setPermissions(initialPerms);
        }
        else if (open) {
            setPermissions({});
        }
    }, [open, modules, initialPermissions, initialOverrides]);
    return (_jsx(Drawer, { className: `form-input-root ${type || ""}`, anchor: "right", open: open, onClose: onClose, sx: {
            width: drawerWidth,
            flexShrink: 0,
            zIndex: 1301, // Higher than UserDrawer (1300)
            '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
            },
        }, children: _jsxs(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: '#fdfdfd'
            }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px',
                        height: '60px',
                        borderBottom: '1px solid #e9eaeb',
                        backgroundColor: '#fafafa'
                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(IconButton, { onClick: onClose, size: "small", disableRipple: true, sx: { p: 0, '&:hover': { backgroundColor: 'transparent' } }, children: _jsx(CloseIcon, { sx: { fontSize: 20, color: '#71717A' } }) }), _jsx("div", { className: "line" }), _jsx(Typography, { sx: {
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: '#18181b',
                                        lineHeight: '24px'
                                    }, className: "drawer-main-title", children: "Override Role" })] }), _jsx(AppButton, { variant: "contained", color: "primary", onClick: handleSave, startIcon: _jsx(SaveIcon, {}), 
                            // startIcon={loading ? <CustomLoader sx={{ color: 'white' }} size={18} /> : <SaveIcon />}
                            disabled: loading || !userId, className: "app-common-button", children: loading ? _jsx(CustomLoader, { sx: { color: 'white' }, size: 18 }) : 'Save' })] }), _jsxs("div", { className: "override-sidepopup-container", children: [_jsx("div", { className: "override-textfield", children: _jsx(TextField, { label: "Role Name", value: roleName, fullWidth: true, disabled: true }) }), _jsx("div", { className: "sidebar-popup-all-permission", children: _jsx(AllPermissions, { modules: modules, permissions: permissions, onChange: handlePermissionsChange, disabled: false }) })] })] }) }));
}
